import React, { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "Shared/util/custom-hooks";
import { PaymentInformationMessageResource } from "Shared/util/types";
import FadeTransition from "Shared/components/transitions/fade-transition";
import Spinner from "Shared/components/spinner";
import TextLink from "Shared/components/text-link";
import { useConsentStatusPoller } from "../hooks";
import styles from "./decoupled-same-device-tab.css";
import { DecoupledSameDeviceTabProps } from "./types";

export const DecoupledSameDeviceTab = ({
    data: { bankIdApplicationUrl, token },
    onError,
    onReturn,
    onUserVerify,
    show,
}: DecoupledSameDeviceTabProps) => {
    const history = useHistory();
    const t = useTranslation((x) => x.edit_payment_information);

    useEffect(() => {
        if (bankIdApplicationUrl) {
            const params = new URLSearchParams();
            const callbacktoken = bankIdApplicationUrl.split(
                encodeURIComponent("callbacktoken="),
            )[1];
            if (callbacktoken) {
                params.set("callbacktoken", callbacktoken);
                history.push({ search: params.toString() });
            }

            window.location.href = bankIdApplicationUrl;
        }
    }, [bankIdApplicationUrl]);

    const handlePollFailedStatus = useCallback(
        (message: PaymentInformationMessageResource | null) =>
            onError(true, message),
        [onError],
    );

    const handlePollFetchError = useCallback(
        (message: PaymentInformationMessageResource | null) =>
            onError(true, message),
        [onError],
    );

    const {
        begin: beginConsentPoller,
        disable: disableConsentPoller,
        enable: enableConsentPoller,
    } = useConsentStatusPoller(
        onUserVerify,
        handlePollFailedStatus,
        handlePollFetchError,
    );

    useEffect(() => {
        if (!show || !token) return;

        enableConsentPoller();
        beginConsentPoller(token);
    }, [beginConsentPoller, enableConsentPoller, show, token]);

    const handleReturn = () => {
        disableConsentPoller();
        onReturn(token);
    };

    return (
        <FadeTransition
            isOpen={show}
            enterDuration={200}
            className={styles.container}
        >
            {!show ? null : (
                <>
                    <h3>{t.identify_with_bankid}</h3>
                    <Spinner customClass={styles.spinner} />
                    <p className={styles.waitingForIdentification}>
                        {t.waiting_for_identification}
                    </p>
                    <TextLink
                        text={t.return}
                        link={handleReturn}
                        className={styles.returnTextLink}
                    />
                </>
            )}
        </FadeTransition>
    );
};
